import React from 'react';
import { Button, ButtonProps } from 'src/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';

type Props = Omit<ButtonProps, 'children'> & {
  icon: React.ReactNode;
  /**
   * Both the accessible name and the tooltip. Include the active count in it — `aria-label`
   * replaces the entire subtree for accessible-name computation, so a count rendered only as
   * the badge below would never be announced (#2614).
   */
  label: string;
  /** Active count. Omit or pass 0 for none. */
  count?: number;
};

/**
 * Trigger for the Filter and Sort controls once they lost their text labels (#2614).
 *
 * Dropping the label costs two things a labelled button had for free: a tooltip, since
 * `ArrowDownUp` is not self-evident, and an announced count. This restores both, and forwards
 * props and ref so it still works as a Radix `asChild` trigger.
 */
export const IconOnlyTriggerButton = React.forwardRef<HTMLButtonElement, Props>(
  ({ icon, label, count, className, ...props }, ref) => (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            type={'button'}
            variant={'outline'}
            size={'icon'}
            aria-label={label}
            className={cn('tw-relative tw-h-11 tw-w-11 tw-rounded-full', className)}
            {...props}
          >
            {icon}
            {!!count && (
              // aria-hidden: the count is already in the accessible name above, so announcing
              // the badge too would repeat it. Width grows with the digits rather than
              // clipping, since the count is unbounded.
              <span
                aria-hidden={'true'}
                className={
                  'tw-absolute -tw-right-1 -tw-top-1 tw-flex tw-h-4 tw-min-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand tw-px-1 tw-text-[10px] tw-font-medium tw-text-white'
                }
              >
                {count > 99 ? '99+' : count}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ),
);
IconOnlyTriggerButton.displayName = 'IconOnlyTriggerButton';
